import {
  BaseTraceContextLog,
  ProcessedTraceLog,
  GroupedTraceLogs,
} from '../models/tracing';

/**
 * Parse JSON/XML event data and context properties
 */
export const processTraceLogs = <T extends BaseTraceContextLog>(logs: T[]): ProcessedTraceLog[] => {
  return logs.map((log) => {
    const processed: ProcessedTraceLog = {
      ...log,
      nestingLevel: 0,
    };

    // Parse context properties (always JSON)
    if (log.contextProperties) {
      try {
        processed.parsedContextProperties = JSON.parse(log.contextProperties);
      } catch (e) {
        // If parsing fails, leave as string
        console.warn('Failed to parse context properties:', e);
      }
    }

    // Determine nesting level from traceGroup
    if (log.traceGroup) {
      const parts = log.traceGroup.split(':');
      processed.nestingLevel = parts.length;
    }

    return processed;
  });
};

/**
 * Sort trace logs by timestamp (frontend sorting as API doesn't guarantee order)
 */
const sortTraceLogsByDateTime = (logs: ProcessedTraceLog[]): ProcessedTraceLog[] => {
  return [...logs].sort((a, b) => {
    const dateA = new Date(a.eventTimestamp).getTime();
    const dateB = new Date(b.eventTimestamp).getTime();
    return dateA - dateB;
  });
};

/**
 * Group trace logs hierarchically by traceGroup
 */
export const groupTraceLogs = (logs: ProcessedTraceLog[]): GroupedTraceLogs[] => {
  const sorted = sortTraceLogsByDateTime(logs);
  const rootLogs: ProcessedTraceLog[] = [];
  const groupMap = new Map<string, ProcessedTraceLog[]>();

  // Separate root logs (no traceGroup) and grouped logs
  sorted.forEach((log) => {
    if (!log.traceGroup) {
      rootLogs.push(log);
    } else {
      const existing = groupMap.get(log.traceGroup) || [];
      existing.push(log);
      groupMap.set(log.traceGroup, existing);
    }
  });

  // Build hierarchical structure
  const groups: GroupedTraceLogs[] = [];

  // Add root logs as individual groups
  rootLogs.forEach((log) => {
    groups.push({
      traceGroup: null,
      traceGroupDescription: null,
      logs: [log],
      children: [],
      nestingLevel: 0,
    });
  });

  // Process grouped logs
  const processedGroupings = new Set<string>();
  
  Array.from(groupMap.keys())
    .sort((a, b) => {
      // Sort by first occurrence timestamp
      const logsA = groupMap.get(a)!;
      const logsB = groupMap.get(b)!;
      const dateA = new Date(logsA[0].eventTimestamp).getTime();
      const dateB = new Date(logsB[0].eventTimestamp).getTime();
      return dateA - dateB;
    })
    .forEach((traceGroup) => {
      if (processedGroupings.has(traceGroup)) return;

      const logs = groupMap.get(traceGroup)!;
      const nestingLevel = traceGroup.split(':').length;

      // Check if this is a nested group (contains ':')
      if (traceGroup.includes(':')) {
        // This is a nested group - find its parent
        const parts = traceGroup.split(':');
        const parentTraceGroup = parts.slice(0, -1).join(':');
        
        // Find parent group
        const parentGroup = findGroupByTraceGroup(groups, parentTraceGroup);
        
        if (parentGroup) {
          // Add as child to parent
          parentGroup.children.push({
            traceGroup,
            traceGroupDescription: logs[0].traceGroupDescription,
            logs,
            children: [],
            nestingLevel,
          });
        } else {
          // Parent not found, add as root
          groups.push({
            traceGroup,
            traceGroupDescription: logs[0].traceGroupDescription,
            logs,
            children: [],
            nestingLevel,
          });
        }
      } else {
        // Top-level group
        groups.push({
          traceGroup,
          traceGroupDescription: logs[0].traceGroupDescription,
          logs,
          children: [],
          nestingLevel,
        });
      }

      processedGroupings.add(traceGroup);
    });

  return groups;
};

/**
 * Helper to find a group by its traceGroup ID
 */
const findGroupByTraceGroup = (groups: GroupedTraceLogs[], traceGroup: string): GroupedTraceLogs | null => {
  for (const group of groups) {
    if (group.traceGroup === traceGroup) {
      return group;
    }
    // Search children recursively
    const found = findGroupByTraceGroup(group.children, traceGroup);
    if (found) return found;
  }
  return null;
};
